import {
  EnablementInfoPayloadV3,
  IEnablementInfoV3,
  SmsCampaignInfo,
  UseCaseData,
  GetVerticalResponse,
} from 'interface/WizardV3';
import { Resend2FAEmailPayload } from 'containers/pages/Messaging/CampaignDialog/WizardContextV3/usePostResend2FAEmail';
import { createClient, safeRequest } from '../clientConfiguration';

export const smsApiClient = createClient({
  baseURL: '/rest-api/sms/v1',
});

export const getTeamPreferences = (teamId: string) => {
  return safeRequest(() => smsApiClient.get(`/teams/${teamId}/preferences`));
};

export const getStockExchanges = async (corpAccountNumber: number) => {
  const response = await smsApiClient.get<{ stockExchanges: string[] }>(
    `/corp-accounts/tcr/enum/stockExchange`,
    {
      headers: { 'nextiva-context-corpAcctNumber': corpAccountNumber },
    },
  );
  return response.data;
};

export const getTCRStateOptions = async () => {
  const response = await smsApiClient.get<{
    usaStatesMap: Record<string, string>;
  }>('/corp-accounts/tcr/enum/usStates');
  return response.data;
};

export const getSMSCampaignStatus = async (corpAcct: number) => {
  const response = await smsApiClient.get<TcrCampaignStatus>(
    `/corp-accounts/${corpAcct}/campaigns/status`,
  );
  return response.data;
};

export const getEnablementInfo = async (corpAcctNumber: number) => {
  const response = await smsApiClient.get<IEnablementInfoV3>(
    `/corp-accounts/${corpAcctNumber}/campaigns/enablement`,
  );
  return response.data;
};
export const getVertical = async (corpAcctNumber: number) => {
  const response = await smsApiClient.get<GetVerticalResponse>(
    `/corp-accounts/tcr/enum/vertical`,
    { headers: { 'nextiva-context-corpAcctNumber': corpAcctNumber } },
  );
  return response.data;
};

export const getUseCases = async (
  corpAcctNumber: number,
  validSubUsecase: string,
) => {
  const response = await smsApiClient.get<UseCaseData>(
    `/corp-accounts/tcr/enum/useCases?validSubUsecase=${validSubUsecase}`,
    {
      headers: {
        'nextiva-context-corpAcctNumber': corpAcctNumber,
      },
    },
  );
  return response.data;
};

export const createEnablementInfo = async (
  corpAcctNumber: number,
  payload: EnablementInfoPayloadV3,
) => {
  const response = await smsApiClient.post<IEnablementInfoV3>(
    `/corp-accounts/${corpAcctNumber}/campaigns/enablement`,
    payload,
    { headers: { 'nextiva-context-corpAcctNumber': corpAcctNumber } },
  );
  return response.data;
};

export const createTCRBrandAndCampaign = async (corpAcctNumber: number) => {
  const response = await smsApiClient.post<CreateTCRBrandAndCampaignResponse>(
    `/corp-accounts/${corpAcctNumber}/campaigns/submit`,
    {},
    { headers: { 'nextiva-context-corpAcctNumber': corpAcctNumber } },
  );
  return response.data;
};

export const updateEnablementInfo = async (
  corpAcctNumber: number,
  payload: EnablementInfoPayloadV3,
) => {
  const response = await smsApiClient.patch<IEnablementInfoV3>(
    `/corp-accounts/${corpAcctNumber}/campaigns/enablement`,
    payload,
    { headers: { 'nextiva-context-corpAcctNumber': corpAcctNumber } },
  );
  return response.data;
};

export const getSmsCampaignInformation = (
  corpAcctNumber: number,
  getCampaignAndCarrierCounts: boolean,
) => {
  return safeRequest(() =>
    smsApiClient.get<SmsCampaignInfo>(
      `/corp-accounts/${corpAcctNumber}/campaigns/metadata?getCampaignAndCarrierCounts=${String(
        getCampaignAndCarrierCounts,
      )}`,
    ),
  );
};

export const deleteEnablementInfo = (corpAcctNumber: number) => {
  return smsApiClient.delete(
    `/corp-accounts/${corpAcctNumber}/campaigns/enablement`,
  );
};

export interface IWebsiteCreationDto {
  companyName: string;
  contactName: string;
  corpAcctNumber: number;
  packageSelected: 'Basic' | 'Pro' | 'Elite';
  phoneNumber: string;
  submitRegistration: string;
  subscriptionType: 'Monthly' | 'Yearly';
  userEmail: string;
}

export const requestWebsiteCreation = async (
  corpAcctNumber: number,
  websiteCreationDto: IWebsiteCreationDto,
) => {
  const response = await smsApiClient.post(
    `/corp-accounts/${corpAcctNumber}/websiteCreation`,
    websiteCreationDto,
  );
  return response;
};

export type AttachmentType = 'mms' | 'supportingDocument';

export const getCampaignAttachment = (
  corpAcctNumber: number,
  attachmentType: AttachmentType,
) => {
  const response = smsApiClient.get(
    `/corp-accounts/${corpAcctNumber}/campaign/attachments/${attachmentType}`,
  );
  return response;
};

export const deleteCampaignAttachment = (
  corpAcctNumber: number,
  attachmentId: string,
) => {
  const response = smsApiClient.delete(
    `/corp-accounts/${corpAcctNumber}/campaign/attachments/${attachmentId}`,
  );
  return response;
};

export const postCampaignAttachment = (
  corpAcctNumber: number,
  attachmentType: AttachmentType,
  files: File[],
  controller: AbortController,
) => {
  const response = smsApiClient.post(
    `/corp-accounts/${corpAcctNumber}/campaign/attachments/${attachmentType}`,
    { files },
    {
      signal: controller.signal,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response;
};

export function postResend2FA({
  corpAcctNumber,
  businessEmailNew,
  restartAuthVettingStatus = false,
}: Resend2FAEmailPayload) {
  let baseUrl = `/corp-accounts/${corpAcctNumber}/brands/2faEmail?restartAuthVettingStatus=${String(
    restartAuthVettingStatus,
  )}`;
  if (businessEmailNew !== null) {
    baseUrl += `&businessEmailNew=${businessEmailNew}`;
  }
  return smsApiClient.post(baseUrl);
}
export function postResendOTP(corpAcctNumber: number) {
  return smsApiClient.post(`/corp-accounts/${corpAcctNumber}/brands/smsOtp`);
}

interface UpdateTeamPreferences {
  corpAcct: number;
  created: string;
  id: string;
  signaturePreference: string;
  teamId: string;
  updated: string;
}

export const updateTeamPreferences = (
  teamId: string,
  body: UpdateTeamPreferences,
) => {
  return safeRequest(() =>
    smsApiClient.put(`/teams/${teamId}/preferences`, body),
  );
};
